import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useApolloClient } from '@apollo/client';
import { useStoreContext } from 'features/store';

import { IRestaurant } from '@rbi-ctg/store';
import {
  IConfigOfferEntryDto,
  IRewardEntryDto,
  ISystemWideOfferEntryDto,
  OneStoreMenuDocument,
  ServiceMode,
  useOneStoreMenuQuery,
} from 'generated/graphql-gateway';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { withSimpleCache } from 'state/graphql/with-simple-cache';
import { useLocale } from 'state/intl';
import { IDayPartBoundary } from 'state/menu/main-menu/types';
import { useServiceModeContext } from 'state/service-mode';
import { getDateInTimezone } from 'utils/dateTime';
import { useMemoAll } from 'utils/use-memo-all';

import { findActiveDayPartIds } from './dayparts';
import {
  IIngredients,
  ILocalizedMenu,
  ILocalizedMenuEntry,
  IMainMenuNode,
  IndexedMenu,
} from './types';
import { IUnifiedMenuContext, UnifiedMenuContext } from './unified-menu-context';
import {
  createIndexedMenu,
  createIndexedOffers,
  createIndexedRewards,
  createMainMenuNode,
  getPosServiceMode,
} from './utils';

const useCachedOneStoreMenuQuery = withSimpleCache(useOneStoreMenuQuery);

export const UnifiedMenuProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const client = useApolloClient();
  const isEnabled = useEnableUnifiedMenu();
  const { serviceMode } = useServiceModeContext();
  const { store, availableDayParts } = useStoreContext();
  const { locale } = useLocale();
  const [hasError, setHasError] = useState(false);

  const [isRefetching, setIsRefetching] = useState<boolean>(false);
  const [menu, setMenu] = useState<ILocalizedMenu | undefined>(undefined);

  const { loading, error, data } = useCachedOneStoreMenuQuery({
    variables: store?.number ? { storeId: store.number } : undefined,
    skip: !isEnabled || !store?.number,
  });

  // Always set the menu when data changes with useEffect because
  // useCachedOneStoreMenuQuery's onComplete is not called when the cache is
  // used
  useEffect(() => {
    if (loading) {
      return;
    }
    if (data?.OneStoreMenu) {
      setMenu(data.OneStoreMenu);
    }
    if (error) {
      setHasError(true);
    }
  }, [loading, data, error]);

  const isLoading = loading || isRefetching;

  const indexedMenu: IndexedMenu<ILocalizedMenuEntry> = useMemo(
    () => (menu ? createIndexedMenu(menu) : IndexedMenu.EMPTY),
    [menu]
  );

  const indexedRewards: IndexedMenu<IRewardEntryDto> = useMemo(
    () => (menu ? createIndexedRewards(menu) : IndexedMenu.EMPTY),
    [menu]
  );
  const indexedOffers: IndexedMenu<IConfigOfferEntryDto | ISystemWideOfferEntryDto> = useMemo(
    () => (menu ? createIndexedOffers(menu) : IndexedMenu.EMPTY),
    [menu]
  );

  const mainMenu = useMemo(
    () => maybeCreateMainMenu(menu, indexedMenu, serviceMode, store, availableDayParts),
    [availableDayParts, indexedMenu, menu, serviceMode, store]
  );

  const refetchMenu = useCallback(async () => {
    try {
      setIsRefetching(true);
      const { data, error } = await client.query({
        query: OneStoreMenuDocument,
        variables: store?.number ? { storeId: store.number } : undefined,
        fetchPolicy: 'no-cache',
      });

      if (data?.OneStoreMenu) {
        setMenu(data.OneStoreMenu);
      }
      if (error) {
        setHasError(true);
      }
    } finally {
      setIsRefetching(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch menu when locale changes to ensure translations are updated.
  // Unlike the legacy menu which uses lazy queries with language in dependencies
  // (triggering automatic refetch via callback recreation), OneMenu uses an
  // automatic Apollo query that only reacts to variable changes. Since locale
  // is passed via HTTP headers (not query variables), Apollo doesn't detect
  // locale changes automatically, so we need to manually refetch when locale changes.
  const prevLocaleRef = useRef(locale);
  useEffect(() => {
    if (prevLocaleRef.current !== locale) {
      refetchMenu();
      prevLocaleRef.current = locale;
    }
  }, [locale, refetchMenu]);

  const value: IUnifiedMenuContext = useMemoAll({
    rawMenu: menu,
    mainMenu: mainMenu?.children ?? EMPTY_MENU,
    indexedMenu,
    indexedRewards,
    indexedOffers,
    isLoading,
    refetchMenu,
    ingredients: (menu?.ingredients as IIngredients[]) ?? [],
    hasError,
  });

  return <UnifiedMenuContext.Provider value={value}>{children}</UnifiedMenuContext.Provider>;
};

const EMPTY_MENU = [];

function maybeCreateMainMenu(
  menu: ILocalizedMenu | undefined,
  indexedMenu: IndexedMenu<ILocalizedMenuEntry> | undefined,
  serviceMode: ServiceMode | null,
  store: IRestaurant | undefined,
  availableDayParts: IDayPartBoundary[] | undefined
): IMainMenuNode | undefined {
  if (indexedMenu && menu && serviceMode && store) {
    const now = store.timezone ? getDateInTimezone(store.timezone) : new Date();
    // TODO In future there should be only one source for this: availableDayParts
    const activeDayPartIds = availableDayParts
      ? availableDayParts.map(dp => dp.key)
      : findActiveDayPartIds(menu.dayParts, now);
    return createMainMenuNode(indexedMenu, menu.rootMenuEntryId, {
      posServiceMode: getPosServiceMode(serviceMode),
      dayPartIds: activeDayPartIds,
    });
  }
  return undefined;
}
